<template>
  <v-container>
    <v-row>
      <v-col class="px-5">
        <br />
        <div class="d-flex align-center">
          <v-icon large>mdi-account-box</v-icon> <span class="text-h6 ml-2">Account</span> <br /><br />
        </div>
        <v-divider></v-divider>
        <div class="mt-2">
          <br />
          <span class="grey--text text--lighten-1">Email </span>
          <br />{{user.attributes.email}}
          <br /><br />
          <span class="grey--text text--lighten-1">Member since </span>
          <br />
          <span>{{formatDate(new Date(user.attributes['custom:createdAt']), 'dd/MM/yyyy')}}</span>
          <br /><br />
          <span class="grey--text text--lighten-1">Password </span>
          <v-btn
            v-if="!showEditPassword"
            @click="showEditPassword = true"
            small
            color="primary"
            class="ml-5"
            outlined
          >edit</v-btn>
          <v-btn
            v-if="showEditPassword"
            @click="clearAndCloseForm"
            small
            color="primary"
            class="ml-5"
            outlined
          >cancel</v-btn>
          <v-btn
            v-if="showEditPassword"
            @click="handleClickSave"
            small
            color="primary"
            class="ml-2"
          >save</v-btn>
          <br />
          <span v-if="!showEditPassword">**********</span> <br />
          <div
            v-if="showEditPassword"
            style="width:300px"
          >
            <v-text-field
              placeholder="xxxxxxxxxx"
              label="Old Password"
              outlined
              dense
              hide-details
              v-model="oldPassword"
              type="password"
              @blur="handleInputBlur(`forms.changePassword.oldPassword`)"
              @input="hideValidationErrorMsg(`forms.changePassword.oldPassword`)"
            ></v-text-field>
            <ValidationErrorMsg :validation-data="getInputValidationData(`forms.changePassword.oldPassword`)" />
            <v-text-field
              placeholder="xxxxxxxxxx"
              label="New Password"
              outlined
              dense
              hide-details
              class="mt-2"
              v-model="newPassword"
              type="password"
              @blur="handleInputBlur(`forms.changePassword.newPassword`)"
              @input="hideValidationErrorMsg(`forms.changePassword.newPassword`)"
            ></v-text-field>
            <ValidationErrorMsg :validation-data="getInputValidationData(`forms.changePassword.newPassword`)" />
            <br />
            <div class="text-body-2">
              Password should include at least:
              <ul>
                <li>8 characters</li>
                <li>a number </li>
                <li>a capital letter</li>
                <li>a lowercase letter</li>
                <li>no spaces at the start or end</li>
              </ul>
            </div>
          </div>
          <br />
          <div class="d-flex flex-row mb-2">
            <div class="grey--text text--lighten-1 mt-1">Two-Factor Authentication</div>
            <div class="text-center">
              <v-switch
                v-if="!isMfaEnabled"
                class="mt-0 ml-5"
                inset
                hide-details
                dense
                v-model="isMfaEnabled"
                @click="enableMfa"
              ></v-switch>
              <v-switch
                v-if="isMfaEnabled"
                class="mt-0 ml-5"
                inset
                hide-details
                dense
                v-model="isMfaEnabled"
                @click="disableMfaTotpStep = 1; showDialog = true"
              ></v-switch>
            </div>
          </div>
          <span>{{ isMfaOn ? 'ON' : 'OFF' }}</span> <br />
        </div>
      </v-col>
    </v-row>
    <v-dialog
      v-model="showDialog"
      max-width="374"
      @click:outside="clearAndCloseForm"
    >
      <v-card
        v-if="enableMfaTotpStep === 1"
        color="cardIsland"
        class="pb-2"
      >
        <v-card-title class="text-h5">
          Set up 2FA
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="mt-2">
          <div class="my-3">
            Use your authentication app to scan the QR code below. If you don't have one, we recommend the
            <span class="text-blue-500 underline"><a
                href="https://support.google.com/accounts/answer/1066447"
                target="_blank"
              >Google</a></span>
            or
            <span class="text-blue-500 underline"><a
                href="https://www.microsoft.com/en-us/security/mobile-authenticator-app"
                target="_blank"
              >Microsoft</a></span> app.
          </div>
          <div
            class="my-3 d-flex flex row"
            style="align-items: center; flex-direction: column"
          >
            <qrcode-vue
              :value="mfaTotpAuth"
              size="200"
            />
            <a
              v-if="!isMfaTotpSetupKeyShown"
              @click="isMfaTotpSetupKeyShown = true"
              class="text-sm text-blue-400"
            >Cannot scan QR code? Click here.</a>
          </div>
          <div v-if="isMfaTotpSetupKeyShown">
            <span>Enter setup key into your app manually:</span>
            <div
              class="py-2 px-2 mt-2 d-flex justify-space-between"
              style="border:solid white 0.5px; border-radius:5px"
            >
              <div style="word-break: break-all;" class="mr-4">
                {{ mfaTotpSetupKey }}
              </div>
              <div>
                <v-icon
                  v-if="isClipboardSupported"
                  @click="copyToClipBoard(mfaTotpSetupKey)"
                  class="ml-4"
                >
                  mdi-content-copy
                </v-icon>
              </div>
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            @click="clearAndCloseForm(); isMfaEnabled = false"
            text
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            @click="enableMfaTotpStep = 2"
          >
            Next
          </v-btn>
        </v-card-actions>
      </v-card>
      <v-card
        v-if="enableMfaTotpStep === 2"
        color="cardIsland"
        class="pb-2"
      >
        <v-card-title class="text-h5">
          Set up 2FA
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="mt-2">
          Enter the 6-digit code displayed in your authentication app below.
          <v-text-field
            v-model="sixDigitToken"
            outlined
            dense
            hide-details
            label="6-digit Code"
            placeholder="XXXXXX"
            class="mt-2"
            type="number"
            @blur="handleInputBlur(`forms.enableMfaTotp.sixDigitToken`)"
            @input="hideValidationErrorMsg(`forms.enableMfaTotp.sixDigitToken`)"
          ></v-text-field>
          <ValidationErrorMsg :validation-data="getInputValidationData(`forms.enableMfaTotp.sixDigitToken`)" />
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            @click="clearAndCloseForm(); isMfaEnabled = false"
            text
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            @click="verifyToken"
            :loading="isLoading"
          >
            Check code
          </v-btn>
        </v-card-actions>
      </v-card>
      <v-card
        v-if="disableMfaTotpStep === 1"
        color="cardIsland"
        class="pb-2"
      >
        <v-card-title class="text-h5">
          Disable 2FA
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="mt-2">
          Are you sure you want to disable 2-factor authentication?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            @click="clearAndCloseForm(); isMfaEnabled = true"
            outlined
          >
            <v-icon left>mdi-window-close</v-icon>
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            @click="disableMfa"
            :loading="isLoading"
          >
            Disable
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-snackbar
      v-model="snackbar.show"
      top
      :color="snackbar.color"
    >
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn
          color="white"
          text
          v-bind="attrs"
          @click="snackbar.show = false"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import { mapState } from 'vuex'
import { Auth } from 'aws-amplify';
import ValidationErrorMsg from '../../components/ValidationErrorMsg.vue'
import { validateValues, _get, _set } from '../../helpers/validation.js';
import QrcodeVue from 'qrcode.vue'
import { utilsMixin } from '../../mixins/utilsMixin.js'

export default {
  components: {
    ValidationErrorMsg,
    QrcodeVue,
  },
  mixins: [utilsMixin],
  data() {
    return {
      showEditPassword: false,
      oldPassword: null,
      newPassword: null,
      snackbar: {
        text: "",
        color: "",
        show: false,
      },
      showDialog: false,
      enableMfaTotpStep: 0,
      disableMfaTotpStep: 0,
      isMfaTotpSetupKeyShown: false,
      mfaTotpSetupKey: "",
      sixDigitToken: null,
      isMfaEnabled: false,
      isMfaOn: false,
      isLoading: false,
      forms: {
        changePassword: {
          oldPassword: {
            path: "oldPassword",
            validationRulesPath: "passwordRules",
            showErrorMsg: false,
          },
          newPassword: {
            path: "newPassword",
            validationRulesPath: "passwordRules",
            showErrorMsg: false,
          },
        },
        enableMfaTotp: {
          sixDigitToken: {
            path: "sixDigitToken",
            validationRulesPath: "sixDigitTokenRules",
            showErrorMsg: false,
          },
        },
      }
    }
  },
  computed: {
    ...mapState("auth", ["user"]),
    passwordRules() {
      return [
        {
          name: 'required',
          validateRule: function (val, rule) {
            return rule.validate(val)
          }
        },
        {
          name: 'password',
          validateRule: function (val, rule) {
            let result = rule.validate(val)
            result.msg = 'Invalid password.'
            return result
          }
        }
      ]
    },
    sixDigitTokenRules() {
      return [
        {
          name: 'required',
          validateRule: function (val, rule) {
            return rule.validate(val)
          }
        },
        {
          name: 'numbersOnlyExact',
          validateRule: function (val, rule) {
            let result = rule.validate(val, 6)
            result.msg = 'Your code should be exactly 6-digits.'
            return result
          }
        }
      ]
    },
    mfaTotpAuth() {
      let label = `EasyCrypto (${this.user.attributes.email})`;
      let key = this.mfaTotpSetupKey;
      return `otpauth://totp/${label}?secret=${key}`;
    },
  },
  methods: {
    handleInputBlur(inputMetadataPath) {
      let { inputMetadata, val } = this.getInputValidationData(inputMetadataPath);
      inputMetadata.showErrorMsg = true;
      if (inputMetadata === null) {
        return;
      }
      if (val === null || val === undefined) {
        _set(this, inputMetadata.path, "");
      }
    },
    getInputValidationData(inputMetadataPath) {
      let inputMetadata = _get(this, inputMetadataPath);
      if (!inputMetadata) console.log("Couldn't find this path:", inputMetadataPath);
      let val = _get(this, inputMetadata.path);
      let rules = _get(this, inputMetadata.validationRulesPath);
      return { inputMetadata, val, rules };
    },
    hideValidationErrorMsg(inputMetadataPath) {
      let { inputMetadata } = this.getInputValidationData(inputMetadataPath);
      inputMetadata.showErrorMsg = false;
    },
    showValidationErrorMsgsByForm(formMetadataName) {
      let formMetadata = _get(this, formMetadataName);
      for (const item of Object.values(formMetadata)) {
        item.showErrorMsg = true;
      }
    },
    validateInputsByForm(formMetadataName) {
      let formMetadata = _get(this, formMetadataName);
      // sanitizing form
      for (const key of Object.keys(formMetadata)) {
        let inputMetadata = formMetadata[key];
        let val = _get(this, inputMetadata.path);
        if (typeof (val) !== 'boolean') {
          val = val === null || val === undefined ? "" : val;
          val = val.trim();
        }
        _set(this, inputMetadata.path, val);
      }
      // validating form
      let valueAndRulesArray = Object.keys(formMetadata).map((i) => {
        let inputMetadata = formMetadata[i];
        let value = _get(this, inputMetadata.path);
        let rules = _get(this, inputMetadata.validationRulesPath);
        return { value, rules };
      });
      return validateValues(valueAndRulesArray);
    },
    handleClickSave() {
      let isFormValid = this.validateInputsByForm('forms.changePassword');
      if (!isFormValid) {
        this.showValidationErrorMsgsByForm('forms.changePassword');
        return;
      }
      Auth.currentAuthenticatedUser()
        .then(user => {
          return Auth.changePassword(user, this.oldPassword, this.newPassword);
        })
        .then(() => {
          this.snackbarSuccess('Successfully reset password')
          this.clearAndCloseForm()
        })
        .catch(() => {
          this.snackbarError('Current password is incorrect.')
        });
    },
    async enableMfa() {
      this.enableMfaTotpStep = 1
      this.showDialog = true
      const user = await Auth.currentAuthenticatedUser()
      console.log('authenticated', user)
      console.log('currentuserinfo', this.user)
      Auth.setupTOTP(user).then((key) => {
        this.mfaTotpSetupKey = key;
      });
    },
    async verifyToken() {
      this.isLoading = true
      try {
        const user = await Auth.currentAuthenticatedUser()
        await Auth.verifyTotpToken(user, this.sixDigitToken)
        await Auth.setPreferredMFA(user, 'TOTP');
        this.snackbarSuccess('2-factor authentication enabled.')
        this.clearAndCloseForm()
        this.isMfaEnabled = true 
        this.isMfaOn = true 
      } catch {
        this.snackbarError('Incorrect 6-digit code.')
      }
      this.isLoading = false
    },
    async disableMfa() {
      try {
        this.isLoading = true
        const user = await Auth.currentAuthenticatedUser()
        await Auth.setPreferredMFA(user, 'NOMFA')
        this.$store.commit('auth/setUser', user)
        this.snackbarSuccess('2-factor authentication disabled.')
        this.clearAndCloseForm()
        this.isMfaEnabled = false 
        this.isMfaOn = false 
        this.isLoading = false
      } catch (error) {
        this.snackbarError(error)
      }
    },
    snackbarError(msg) {
      this.snackbar.show = true
      this.snackbar.color = 'red'
      this.snackbar.text = msg
    },
    snackbarSuccess(msg) {
      this.snackbar.show = true
      this.snackbar.color = 'green'
      this.snackbar.text = msg
    },
    clearAndCloseForm() {
      this.showDialog = false
      // change password
      this.showEditPassword = false
      this.oldPassword = null
      this.newPassword = null
      // mfa
      this.sixDigitToken = null
      this.isMfaTotpSetupKeyShown = false
      this.enableMfaTotpStep = 0
      this.disableMfaTotpStep = 0
      this.isMfaEnabled = this.isMfaOn
    },
    setMfaType() {
      console.log("hello")
    }
  },
  async mounted() {
    const currentAuthenticatedUser = await Auth.currentAuthenticatedUser()
    console.log('Account page mounted. currentAuthUser is:', currentAuthenticatedUser)
    if (currentAuthenticatedUser.preferredMFA === 'SOFTWARE_TOKEN_MFA') {
      this.isMfaEnabled = true
      this.isMfaOn = true
    }
  }
}
</script>
